import { useMemo, useState } from "react";
import { Box, Button, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, Typography } from "@mui/material";
import SortHeader from "./SortHeader";
import { compareNullsLast, winRate, type SortValue } from "./sort";
import { formatDuration } from "./formatDuration";
import type { EvidenceActivityAgentRow, EvidenceActivityDir } from "../../types/evidenceActivity";

interface Column {
  key: string;
  label: string;
  more?: boolean;
  value: (a: EvidenceActivityAgentRow) => SortValue;
  render: (a: EvidenceActivityAgentRow) => string;
}

const pct = (share: number | null) => (share === null ? "—" : `${Math.round(share * 100)}%`);

const COLUMNS: Column[] = [
  { key: "finished", label: "Submitted", value: (a) => a.finished, render: (a) => String(a.finished) },
  { key: "won", label: "Won %", value: (a) => winRate(a), render: (a) => pct(winRate(a)) },
  { key: "median", label: "Median", value: (a) => a.medianSeconds, render: (a) => formatDuration(a.medianSeconds) },
  { key: "perHour", label: "Per hour", value: (a) => a.perHour, render: (a) => a.perHour.toFixed(1) },
  { key: "abandoned", label: "Abandoned", more: true, value: (a) => a.abandoned, render: (a) => String(a.abandoned) },
  { key: "p90", label: "p90", more: true, value: (a) => a.p90Seconds, render: (a) => formatDuration(a.p90Seconds) },
  { key: "regen", label: "Regen", more: true, value: (a) => a.generatesPerPackage, render: (a) => `${a.generatesPerPackage}×` },
  { key: "screenshots", label: "Screenshots", more: true, value: (a) => a.screenshotsPerPackage, render: (a) => String(a.screenshotsPerPackage) },
  { key: "rewrites", label: "AI rewrites", more: true, value: (a) => a.rewritesPerPackage, render: (a) => String(a.rewritesPerPackage) },
  { key: "time", label: "Time", value: (a) => a.timeSeconds, render: (a) => formatDuration(a.timeSeconds) },
];
const NAME: Column = { key: "name", label: "Agent", value: (a) => a.name, render: (a) => a.name };

const initials = (name: string) => name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]?.toUpperCase()).join("");
const cellSx = { "& td": { fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" } } as const;

export default function AgentsTable({ agents, total }: { agents: EvidenceActivityAgentRow[]; total: EvidenceActivityAgentRow }) {
  const [sort, setSort] = useState("time");
  const [dir, setDir] = useState<EvidenceActivityDir>("desc");
  const [more, setMore] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const columns = useMemo(() => COLUMNS.filter((c) => more || !c.more), [more]);
  const sorted = useMemo(() => {
    const column = [NAME, ...COLUMNS].find((c) => c.key === sort) ?? COLUMNS[COLUMNS.length - 1];
    return [...agents].sort((a, b) => compareNullsLast(column.value(a), column.value(b), dir) || a.name.localeCompare(b.name));
  }, [agents, sort, dir]);
  const lastPage = Math.max(0, Math.ceil(agents.length / pageSize) - 1);
  const current = Math.min(page, lastPage);
  const visible = sorted.slice(current * pageSize, (current + 1) * pageSize);

  const choose = (key: string) => {
    if (sort === key) { setDir(dir === "desc" ? "asc" : "desc"); return; }
    setSort(key);
    setDir(key === "name" ? "asc" : "desc");
    setPage(0);
  };

  return (
    <>
      <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 1.5 }}>
        <Typography sx={{ fontSize: 15, fontWeight: 600 }}>Per agent</Typography>
        <Stack direction="row" spacing={2} alignItems="baseline">
          <Typography variant="caption" color="text.disabled">submitted packages in range</Typography>
          <Button size="small" aria-expanded={more} onClick={() => setMore(!more)} sx={{ textTransform: "none", fontSize: 12, p: 0, minWidth: 0 }}>{more ? "Fewer columns" : "More columns"}</Button>
        </Stack>
      </Stack>
      <TableContainer>
        <Table size="small" aria-label="Per agent">
          <TableHead><TableRow>
            <SortHeader label={NAME.label} active={sort === NAME.key} dir={dir} onClick={() => choose(NAME.key)} />
            {columns.map((c) => <SortHeader key={c.key} label={c.label} active={sort === c.key} dir={dir} onClick={() => choose(c.key)} align="right" />)}
          </TableRow></TableHead>
          <TableBody>
            {visible.map((a) => (
              <TableRow key={a.userId} sx={cellSx}>
                <TableCell>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Box sx={{ width: 24, height: 24, borderRadius: "50%", bgcolor: "primary.light", color: "primary.contrastText", fontSize: 11, fontWeight: 600, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>{initials(a.name)}</Box>
                    <span>{a.name}</span>
                  </Stack>
                </TableCell>
                {columns.map((c) => <TableCell key={c.key} align="right">{c.render(a)}</TableCell>)}
              </TableRow>
            ))}
            <TableRow sx={{ ...cellSx, "& td": { ...cellSx["& td"], fontWeight: 600, borderTop: "2px solid", borderTopColor: "divider", borderBottom: 0 } }}>
              <TableCell>{total.name}</TableCell>
              {columns.map((c) => <TableCell key={c.key} align="right">{c.render(total)}</TableCell>)}
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        rowsPerPageOptions={[10, 25, 50]}
        count={agents.length}
        page={current}
        rowsPerPage={pageSize}
        onPageChange={(_, next) => setPage(next)}
        onRowsPerPageChange={(e) => { setPageSize(Number(e.target.value)); setPage(0); }}
      />
      <Typography variant="caption" color="text.disabled" sx={{ display: "block", mt: 1.5 }}>
        Per hour = submitted packages ÷ counted time. Regen, screenshots and AI rewrites are averages per submitted package.
      </Typography>
    </>
  );
}
